import React, { useState } from "react";
import { Photo, SyncStatus } from "../types/Photo";
import PhotoThumbnail from "./PhotoThumbnail";
import PhotoPreviewModal from "./PhotoPreviewModal";

interface ThumbnailGridProps {
  photos: Photo[];
  onDelete: (id: string) => void;
}

/**
 * La confirmation de suppression, qui dit ce que le geste coûte vraiment.
 *
 * Deux textes, parce que les deux suppressions n'ont rien de commun : celle
 * d'une photo qui n'est pas partie détruit la seule copie qui existe, celle
 * d'une photo arrivée à destination ne fait que libérer l'appareil. Un seul
 * texte pour les deux laisserait croire qu'on efface l'original.
 */
const DeletePhotoModal: React.FC<{
  photo: Photo;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ photo, onConfirm, onCancel }) => {
  const isSent = photo.syncStatus === SyncStatus.SYNCED;

  return (
    <dialog className="modal modal-open" onClose={onCancel}>
      <div className="modal-box">
        <h3 className="font-bold text-lg">
          {isSent
            ? "Retirer cette photo de l'appareil ?"
            : "Supprimer cette photo ?"}
        </h3>
        <p className="py-4">
          {isSent
            ? "Elle est arrivée à destination et y reste : seule la copie de l'appareil est retirée."
            : "Elle n'a pas encore été envoyée : elle n'existe nulle part ailleurs, et la suppression est définitive."}
        </p>
        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onCancel}>
            Annuler
          </button>
          <button className="btn btn-error" onClick={onConfirm}>
            {isSent ? "Retirer" : "Supprimer"}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onCancel}>Annuler</button>
      </form>
    </dialog>
  );
};

const ThumbnailGrid: React.FC<ThumbnailGridProps> = ({ photos, onDelete }) => {
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
  // La suppression est un geste d'exception — celle du plafond ou du pouce —,
  // une photo à la fois et jamais sans confirmation.
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-base-content/70">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p>Aucune photo capturée</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {photos.map((photo) => (
          <PhotoThumbnail
            key={photo.id}
            photo={photo}
            onDelete={setPhotoToDelete}
            onPreview={setPreviewPhoto}
          />
        ))}
      </div>

      <PhotoPreviewModal
        photo={previewPhoto}
        onClose={() => setPreviewPhoto(null)}
      />

      {photoToDelete && (
        <DeletePhotoModal
          photo={photoToDelete}
          onConfirm={() => {
            onDelete(photoToDelete.id);
            setPhotoToDelete(null);
          }}
          onCancel={() => setPhotoToDelete(null)}
        />
      )}
    </div>
  );
};

export default ThumbnailGrid;
